import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { articlesAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { StarRating } from './StarRating';
import { MessageSquare, Trash2, Loader2, Send, LogIn } from 'lucide-react';
import { toast } from 'sonner';

export function ArticleFeedback({ articleId }) {
  const { user } = useAuth();
  const isAdmin = user?.roles?.includes('Admin');
  const [summary, setSummary] = useState({ avg: 0, count: 0, distribution: {}, my_rating: null });
  const [comments, setComments] = useState([]);
  const [texto, setTexto] = useState('');
  const [sending, setSending] = useState(false);
  const [rating, setRating] = useState(false);

  const load = useCallback(async () => {
    try {
      const [r, c] = await Promise.all([articlesAPI.ratings(articleId), articlesAPI.comments(articleId)]);
      setSummary(r.data);
      setComments(c.data);
    } catch {}
  }, [articleId]);

  useEffect(() => { load(); }, [load, user?.id]);

  const handleRate = async (n) => {
    setRating(true);
    try {
      const res = await articlesAPI.rate(articleId, n);
      setSummary(res.data);
      toast.success(`Calificaste este articulo con ${n} estrella${n > 1 ? 's' : ''}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo guardar tu calificacion');
    }
    setRating(false);
  };

  const handleComment = async (e) => {
    e.preventDefault();
    if (!texto.trim()) return;
    setSending(true);
    try {
      const res = await articlesAPI.addComment(articleId, texto.trim());
      setComments(c => [res.data, ...c]);
      setTexto('');
      toast.success('Comentario publicado');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo publicar el comentario');
    }
    setSending(false);
  };

  const handleDelete = async (commentId) => {
    if (!window.confirm('Eliminar este comentario?')) return;
    try {
      await articlesAPI.deleteComment(articleId, commentId);
      setComments(c => c.filter(x => x.id !== commentId));
      toast.success('Comentario eliminado');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo eliminar');
    }
  };

  const total = summary.count || 0;

  return (
    <section className="mt-12 pt-10 border-t border-gray-100" data-testid="article-feedback">
      {/* Rating */}
      <div className="grid md:grid-cols-[1fr_auto] gap-8 items-start">
        <div>
          <h2 className="text-lg font-serif font-semibold text-slate-dark">Calificacion de lectores</h2>
          <div className="flex items-end gap-4 mt-3">
            <span className="text-4xl font-sans font-bold text-slate-dark leading-none" data-testid="rating-avg">{total ? summary.avg.toFixed(1) : '—'}</span>
            <div>
              <StarRating value={summary.avg} readOnly testId="rating-avg-stars" />
              <p className="text-xs font-sans text-gray-400 mt-1" data-testid="rating-count">
                {total ? `${total} calificacion${total > 1 ? 'es' : ''}` : 'Aun sin calificaciones'}
              </p>
            </div>
          </div>
          {total > 0 && (
            <div className="mt-4 space-y-1 max-w-xs" data-testid="rating-distribution">
              {[5, 4, 3, 2, 1].map(n => {
                const c = summary.distribution?.[String(n)] || 0;
                return (
                  <div key={n} className="flex items-center gap-2 text-[11px] font-sans text-gray-400">
                    <span className="w-3 text-right">{n}</span>
                    <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                      <div className="h-full bg-amber-400 rounded-full transition-all" style={{ width: `${total ? (c / total) * 100 : 0}%` }} />
                    </div>
                    <span className="w-5">{c}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="p-5 bg-gray-50 border border-gray-100 rounded-sm md:min-w-[260px]" data-testid="rate-box">
          {user ? (
            <>
              <p className="text-sm font-sans font-medium text-slate-dark">{summary.my_rating ? 'Tu calificacion' : 'Califica este articulo'}</p>
              <div className="mt-2 flex items-center gap-2">
                <StarRating value={summary.my_rating || 0} onRate={handleRate} size="lg" readOnly={rating} testId="my-rating" />
                {rating && <Loader2 className="w-4 h-4 animate-spin text-gray-400" />}
              </div>
              <p className="text-[11px] font-sans text-gray-400 mt-2">{summary.my_rating ? 'Puedes cambiarla cuando quieras.' : 'Una calificacion por persona.'}</p>
            </>
          ) : (
            <>
              <p className="text-sm font-sans font-medium text-slate-dark">Inicia sesion para calificar</p>
              <Button asChild size="sm" variant="outline" className="font-sans mt-3" data-testid="rate-login-btn">
                <Link to="/login"><LogIn className="w-3.5 h-3.5 mr-1.5" /> Iniciar sesion</Link>
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Comments */}
      <div className="mt-12">
        <h2 className="text-lg font-serif font-semibold text-slate-dark flex items-center gap-2">
          <MessageSquare className="w-4 h-4 text-trans-blue" strokeWidth={1.5} /> Comentarios
          <span className="text-sm font-sans font-normal text-gray-400" data-testid="comments-count">({comments.length})</span>
        </h2>

        {user ? (
          <form onSubmit={handleComment} className="mt-4" data-testid="comment-form">
            <Textarea
              value={texto}
              onChange={e => setTexto(e.target.value)}
              placeholder="Comparte tu lectura, preguntas o aportes sobre este articulo..."
              className="font-sans text-sm min-h-[90px]"
              maxLength={2000}
              data-testid="comment-input"
            />
            <div className="flex items-center justify-between mt-2">
              <span className="text-[11px] font-sans text-gray-400">{texto.length}/2000</span>
              <Button type="submit" size="sm" className="bg-slate-dark hover:bg-slate-dark/90 font-sans" disabled={sending || !texto.trim()} data-testid="comment-submit-btn">
                {sending ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1.5" />} Publicar comentario
              </Button>
            </div>
          </form>
        ) : (
          <p className="mt-4 text-sm font-sans text-gray-500" data-testid="comment-login-hint">
            <Link to="/login" className="text-trans-blue hover:underline">Inicia sesion</Link> o <Link to="/registro" className="text-trans-blue hover:underline">registrate</Link> para dejar un comentario.
          </p>
        )}

        <ul className="mt-6 space-y-4" data-testid="comments-list">
          {comments.length === 0 && (
            <li className="text-sm font-sans text-gray-400" data-testid="comments-empty">Se la primera persona en comentar este articulo.</li>
          )}
          {comments.map((c, i) => (
            <li key={c.id} className="p-4 bg-white border border-gray-100 rounded-sm" data-testid={`comment-${i}`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-sm font-sans font-medium text-slate-dark">{c.user_name}</p>
                  <p className="text-[11px] font-sans text-gray-400">{new Date(c.created_at).toLocaleString('es-MX', { dateStyle: 'medium', timeStyle: 'short' })}</p>
                </div>
                {(isAdmin || c.user_id === user?.id) && (
                  <button onClick={() => handleDelete(c.id)} className="text-gray-300 hover:text-red-500 transition-colors" aria-label="Eliminar comentario" data-testid={`delete-comment-${i}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
              <p className="mt-2 text-sm font-serif text-gray-700 leading-relaxed whitespace-pre-wrap">{c.texto}</p>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
